import React from 'react'
import { FastField, Field, useFormikContext } from 'formik'
import _ from 'lodash'
import { Grid, Typography, Box, FormHelperText } from '@mui/material'
import {
  CustomRadio,
  CustomRadioGroup,
  CustomTextField,
  FormDatePicker,
  CustomCheckboxWithoutInitialValue,
} from 'src/form'
import BillingCodes from 'src/participantHistory/intakeNote/BillingCodes'
import { MonthlySummaryNoteFormType } from 'src/participantHistoryForm/form/monthlySummaryNote/MonthlySummaryNoteFormSchema'
import { HealthQuestionnaire } from 'src/participantHistory/shared/HealthQuestionnaire'

const MonthlySummaryNoteSection: React.FC<{ title: string; children: React.ReactNode }> = (props) => (
  <Box mt={4}>
    <Typography variant="h2">{props.title}</Typography>
    <Grid container columnSpacing={2.5} rowSpacing={0}>
      {props.children}
    </Grid>
  </Box>
)

const MonthlySummaryNoteSubSection: React.FC<{
  title: string
  direction?: 'row' | 'column'
  children: React.ReactNode
}> = (props) => (
  <Grid item xs={12} mt={4}>
    <Typography variant="h4">{props.title}</Typography>
    <Box sx={{ display: 'flex', flexDirection: props.direction || 'row', gap: 1 }}>{props.children}</Box>
  </Grid>
)

const interventions = [
  { name: 'PHQ9_GAD7', label: 'PHQ9 and GAD7 offered biweekly to assess for progress.' },
  { name: 'BIOFEEDBACK', label: 'Utilize heart rate device for biofeedback breathing practices.' },
  { name: 'IN_APP_CHAT', label: 'Engage in daily in-app chat (Mon–Fri) with a licensed therapist/psychologist.' },
  { name: 'CBT_MINDFULLNESS_ACT', label: 'CBT, Mindfulness, and ACT practices offered daily.' },
  {
    name: 'PSYCHOEDUCATION',
    label: 'Psychoeducation on cognitive and emotional health topics including sleep and nutrition.',
  },
  {
    name: 'TELEHEALTH_VIDEO',
    label:
      'Optional telehealth video sessions with licensed provider offered within 10 business days of last appointment.',
  },
  { name: 'PEER_SUPPORT', label: 'Access to an anonymous, confidential peer support space.' },
]

type MonthlySummaryNoteFieldsProps = {
  viewOnly?: boolean
  isKaiser: boolean
}

export const MonthlySummaryNoteFields = ({ viewOnly, isKaiser }: MonthlySummaryNoteFieldsProps) => {
  const { values, errors, submitCount } = useFormikContext<MonthlySummaryNoteFormType>()
  const memberIdLabel = isKaiser ? 'Kaiser MRN' : 'Insurance member ID'
  const participantResponseIsOther = _.get(values, 'progressAssessment.participantResponse.response') === 'other'
  const planIsOther = _.get(values, 'progressAssessment.plan.planType') === 'other'
  const riskAssessmentHasChanges = _.get(values, 'progressAssessment.riskAssessment.status') === 'changes'
  const showAssessmentDetails =
    _.get(values, 'progressAssessment.sinceLastAssessment.assessment.OVERALL_WORSENING') ||
    _.get(values, 'progressAssessment.sinceLastAssessment.assessment.FLUCTUATING_PATTERN')
  const showRemindersSent = _.get(
    values,
    'progressAssessment.sinceLastAssessment.assessment.NOT_COMPLETED_QUESTIONNAIRES'
  )

  return (
    <>
      <MonthlySummaryNoteSection title="Overall Information">
        <MonthlySummaryNoteSubSection title="Participant">
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} name="participant.firstName" label="First Name" fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} name="participant.lastName" label="Last Name" fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} name="participant.memberID" label={memberIdLabel} fullWidth />
          </Grid>
        </MonthlySummaryNoteSubSection>

        <MonthlySummaryNoteSubSection title="Assessing Practitioner">
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} name="assessingPractitioner.name" label="Name" fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField
              component={CustomTextField}
              name="assessingPractitioner.licenseType"
              label="License Type"
              fullWidth
            />
          </Grid>
        </MonthlySummaryNoteSubSection>

        {isKaiser && (
          <MonthlySummaryNoteSubSection title="CPT Code">
            <Grid item xs={12} md={4}>
              <FastField component={CustomTextField} name="cptCode" label="Monthly Case Rate Code" fullWidth />
            </Grid>
          </MonthlySummaryNoteSubSection>
        )}

        <MonthlySummaryNoteSubSection title="Dates of Service">
          <Grid item xs={12} md={4}>
            <FastField
              component={FormDatePicker}
              name="datesOfService.startDate"
              label="Start Date"
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={FormDatePicker} name="datesOfService.endDate" label="End Date" fullWidth disabled />
          </Grid>
        </MonthlySummaryNoteSubSection>
      </MonthlySummaryNoteSection>

      <MonthlySummaryNoteSection title="Treatment Goals">
        <Grid item xs={12}>
          <FastField
            fullWidth
            component={CustomTextField}
            name="treatmentPlan.smartGoals"
            label="SMART goals"
            multiline
            minRows={4}
          />
        </Grid>
      </MonthlySummaryNoteSection>

      <MonthlySummaryNoteSection title="Billing and Diagnosis Codes">
        <Grid item xs={12}>
          <BillingCodes name="billingCodes" viewOnly={viewOnly} />
        </Grid>
      </MonthlySummaryNoteSection>

      <MonthlySummaryNoteSection title="Interventions">
        <Grid item xs={12}>
          {interventions.map((intervention) => (
            <FastField
              key={`interventions.${intervention.name}`}
              fullWidth
              component={CustomCheckboxWithoutInitialValue}
              name={`interventions.${intervention.name}`}
              disabled
              label={intervention.label}
            />
          ))}
        </Grid>
      </MonthlySummaryNoteSection>

      <MonthlySummaryNoteSection title="Reason for Seeking Treatment">
        <MonthlySummaryNoteSubSection title="Functional Impairments" direction="column">
          <Grid item xs={12}>
            <FastField
              fullWidth
              component={CustomCheckboxWithoutInitialValue}
              name="reasonForSeekingTreatment.functionalImpairments.DIFFICULTY_MEETING_EXPECTATIONS"
              label="Difficulty meeting work/school/family expectations"
            />
          </Grid>
          <Grid item xs={12}>
            <FastField
              fullWidth
              component={CustomCheckboxWithoutInitialValue}
              name="reasonForSeekingTreatment.functionalImpairments.STRUGGLE_WITH_RELATIONSHIPS"
              label="Struggle with relationships"
            />
          </Grid>
          <Grid item xs={12}>
            <FastField
              fullWidth
              component={CustomCheckboxWithoutInitialValue}
              name="reasonForSeekingTreatment.functionalImpairments.NEGLECT_OF_PERSONAL_NEEDS"
              label="Neglect of personal needs"
            />
          </Grid>
          <Grid item xs={12}>
            <FastField
              fullWidth
              component={CustomCheckboxWithoutInitialValue}
              name="reasonForSeekingTreatment.functionalImpairments.DIFFICULTY_IN_SLEEP"
              label="Difficulty in sleep"
            />
          </Grid>
          <Grid item xs={12}>
            <FastField
              fullWidth
              component={CustomCheckboxWithoutInitialValue}
              name="reasonForSeekingTreatment.functionalImpairments.CHANGE_IN_APPETITE_OR_WEIGHT"
              label="Change in appetite or weight"
            />
          </Grid>
          <Grid item xs={12}>
            <FastField
              fullWidth
              component={CustomCheckboxWithoutInitialValue}
              name="reasonForSeekingTreatment.functionalImpairments.IMPAIRED_COGNITIVE_FUNCTIONING"
              label="Impaired cognitive functioning"
            />
          </Grid>
          <Grid item xs={12}>
            <FastField
              fullWidth
              component={CustomCheckboxWithoutInitialValue}
              name="reasonForSeekingTreatment.functionalImpairments.VOLATILE_MOOD_RESPONSES"
              label="Volatile mood responses"
            />
          </Grid>
          <Grid item xs={12}>
            <FastField
              fullWidth
              component={CustomCheckboxWithoutInitialValue}
              name="reasonForSeekingTreatment.functionalImpairments.DIFFICULTY_MANAGING_TASKS"
              label="Difficulty managing tasks"
            />
          </Grid>
          <Grid item xs={12}>
            <FastField
              fullWidth
              component={CustomCheckboxWithoutInitialValue}
              name="reasonForSeekingTreatment.functionalImpairments.LOSS_OF_INTEREST"
              label="Loss of interest"
            />
          </Grid>
          <Grid item xs={12}>
            <FastField
              fullWidth
              component={CustomTextField}
              name="reasonForSeekingTreatment.functionalImpairments.OTHER"
              label="Other"
              multiline
              minRows={2}
            />
          </Grid>
          {submitCount > 0 && errors.reasonForSeekingTreatment?.functionalImpairments && (
            <FormHelperText error>
              Please select at least one functional impairment from the list or write one that is not listed.
            </FormHelperText>
          )}
        </MonthlySummaryNoteSubSection>
      </MonthlySummaryNoteSection>

      <MonthlySummaryNoteSection title="Participant Engagement">
        <Grid item xs={12}>
          <FastField
            component={CustomTextField}
            name="participantEngagement.participation"
            label="Participation"
            multiline
            minRows={10}
            fullWidth
            placeholder="List of completed practices for the month broken down by type"
          />
        </Grid>
        <Grid item xs={12}>
          <FastField
            component={CustomTextField}
            name="participantEngagement.chatSummary"
            label="Chat Summary"
            multiline
            minRows={4}
            fullWidth
            placeholder="Briefly summarize overall themes from chat communications"
          />
        </Grid>
      </MonthlySummaryNoteSection>

      <MonthlySummaryNoteSection title="Progress Assessment">
        <MonthlySummaryNoteSubSection title="Since Last Assessment" direction="column">
          <Grid item xs={12} mt={2}>
            <HealthQuestionnaire fieldPath="progressAssessment.sinceLastAssessment.healthQuestionnaire" />
          </Grid>
          <Grid item xs={12} mt={2}>
            <Typography variant="h5" gutterBottom>
              Assessment
            </Typography>
            <FastField
              fullWidth
              component={CustomCheckboxWithoutInitialValue}
              name="progressAssessment.sinceLastAssessment.assessment.NO_NOTABLE_CHANGE"
              label="No notable change in symptoms over past 30 days"
            />
            <FastField
              fullWidth
              component={CustomCheckboxWithoutInitialValue}
              name="progressAssessment.sinceLastAssessment.assessment.OVERALL_IMPROVEMENT"
              label="Overall symptom improvement"
            />
            <FastField
              fullWidth
              component={CustomCheckboxWithoutInitialValue}
              name="progressAssessment.sinceLastAssessment.assessment.OVERALL_WORSENING"
              label="Overall symptom worsening"
            />
            <FastField
              fullWidth
              component={CustomCheckboxWithoutInitialValue}
              name="progressAssessment.sinceLastAssessment.assessment.FLUCTUATING_PATTERN"
              label="Fluctuating symptom pattern"
            />
            <FastField
              fullWidth
              component={CustomCheckboxWithoutInitialValue}
              name="progressAssessment.sinceLastAssessment.assessment.NOT_COMPLETED_QUESTIONNAIRES"
              label="Participant has not completed health questionnaires"
            />
            {showRemindersSent && (
              <Box sx={{ pl: 4 }}>
                <FastField
                  fullWidth
                  component={CustomCheckboxWithoutInitialValue}
                  name="progressAssessment.sinceLastAssessment.assessment.REMINDERS_SENT"
                  label="Provider has sent reminders and encouragement to complete"
                />
              </Box>
            )}
          </Grid>
          {showAssessmentDetails && (
            <Grid item xs={12}>
              <FastField
                fullWidth
                component={CustomTextField}
                name="progressAssessment.sinceLastAssessment.assessment.details"
                label="Details"
                multiline
                minRows={4}
              />
            </Grid>
          )}
        </MonthlySummaryNoteSubSection>

        <MonthlySummaryNoteSubSection title="Risk Assessment" direction="column">
          <Grid item xs={12}>
            <Field component={CustomRadioGroup} name="progressAssessment.riskAssessment.status" fullWidth>
              <CustomRadio label="No known changes to risk level since last encounter" value="noChanges" />
              <CustomRadio label="Changes to risk level" value="changes" />
            </Field>
          </Grid>
          {riskAssessmentHasChanges && (
            <Grid item xs={12}>
              <FastField
                fullWidth
                component={CustomTextField}
                name="progressAssessment.riskAssessment.details"
                label="Details"
                multiline
                minRows={4}
              />
            </Grid>
          )}
        </MonthlySummaryNoteSubSection>

        <MonthlySummaryNoteSubSection title="Participant Response" direction="column">
          <Grid item xs={12}>
            <Field component={CustomRadioGroup} name="progressAssessment.participantResponse.response" fullWidth>
              <CustomRadio
                label="Participant is actively engaged in program content and therapist's interventions"
                value="activelyEngaged"
              />
              <CustomRadio
                label="Participant is minimally engaged but reports no other interventions needed at this time"
                value="minimallyEngaged"
              />
              <CustomRadio label="Other (specify engagement level)" value="other" />
            </Field>
          </Grid>
          {participantResponseIsOther && (
            <Grid item xs={12}>
              <FastField
                fullWidth
                component={CustomTextField}
                name="progressAssessment.participantResponse.details"
                label="Details"
                multiline
                minRows={4}
              />
            </Grid>
          )}
        </MonthlySummaryNoteSubSection>

        <MonthlySummaryNoteSubSection title="Plan" direction="column">
          <Grid item xs={12}>
            <Field component={CustomRadioGroup} name="progressAssessment.plan.planType" fullWidth>
              <CustomRadio
                label="Therapist will continue to support client via app chat and offer check in calls as needed"
                value="continueSupport"
              />
              <CustomRadio label="Other" value="other" />
            </Field>
          </Grid>
          {planIsOther && (
            <Grid item xs={12}>
              <FastField
                fullWidth
                component={CustomTextField}
                name="progressAssessment.plan.details"
                label="Details"
                multiline
                minRows={4}
              />
            </Grid>
          )}
        </MonthlySummaryNoteSubSection>
      </MonthlySummaryNoteSection>
    </>
  )
}
